import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, MapPin, Phone, Lock } from 'lucide-react';
import { subscribeEmail } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { language } = useLanguage();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await subscribeEmail(email, null, 'footer');
      toast({
        title: t('common.success', language),
        description: language === 'en' ? 'Thank you for subscribing to our newsletter.' : 'Gracias por suscribirte a nuestro boletín.',
      });
      setEmail('');
    } catch (error) {
      toast({
        title: t('common.error', language),
        description: error.response?.data?.detail || (language === 'en' ? 'Failed to subscribe. Please try again.' : 'Error al suscribirse. Por favor intenta de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white" data-testid="main-footer">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.quickLinks', language)}</h3>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm">{t('nav.about', language)}</Link></li>
              <li><Link to="/programs" className="text-gray-400 hover:text-white transition-colors text-sm">{t('nav.programs', language)}</Link></li>
              <li><Link to="/media" className="text-gray-400 hover:text-white transition-colors text-sm">{t('nav.stories', language)}</Link></li>
              {/* TEMPORARILY HIDDEN - Coffee footer link hidden per user request, restore when ready */}
              {/* <li><Link to="/coffee" className="text-gray-400 hover:text-white transition-colors text-sm">{t('nav.coffee', language)}</Link></li> */}
              <li><Link to="/get-involved" className="text-gray-400 hover:text-white transition-colors text-sm">{t('nav.getInvolved', language)}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.contact', language)}</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Mail size={16} className="mt-1 text-gray-400" />
                <a href="mailto:colombia@vmissions.org" className="text-gray-400 hover:text-white transition-colors text-sm">
                  colombia@vmissions.org
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin size={16} className="mt-1 text-gray-400" />
                <span className="text-gray-400 text-sm">Ibagué, Colombia</span>
              </li>
              <li className="flex items-start space-x-3">
                <Phone size={16} className="mt-1 text-gray-400" />
                <div className="text-sm">
                  <a href="tel:+18137899362" className="text-gray-400 hover:text-white transition-colors block">+1 (813) 789-9362</a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Heart size={16} className="mt-1 text-sh-primary" />
                <a href="https://colombia.vmissions.org" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm">
                  colombia.vmissions.org
                </a>
              </li>
            </ul>
          </div>

          {/* Stay Updated */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{language === 'en' ? 'Stay Updated' : 'Mantente Informado'}</h3>
            <p className="text-gray-400 text-sm mb-4">
              {t('footer.newsletterText', language)}
            </p>
            <form onSubmit={handleSubscribe} className="space-y-2" data-testid="footer-newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('common.emailPlaceholder', language)}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                disabled={loading}
                data-testid="footer-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 bg-sh-primary hover:bg-sh-bright rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                data-testid="footer-subscribe-button"
              >
                {loading ? t('common.submitting', language) : t('footer.subscribe', language)}
              </button>
            </form>
          </div>

          {/* Give with Confidence */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{language === 'en' ? 'Give with Confidence' : 'Done con Confianza'}</h3>
            <div className="flex items-start space-x-3">
              <Lock size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-400 text-sm leading-relaxed">
                {language === 'en' 
                  ? "We're an IRS Recognized 501(c)(3) Tax-Exempt Charitable Organization. All donations are tax-deductible."
                  : 'Somos una organización caritativa exenta de impuestos 501(c)(3) reconocida por el IRS. Todas las donaciones son deducibles de impuestos.'}
              </p>
            </div>
          </div>

          {/* The Safe House Logo & Description */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_7470f516-1d16-4323-b125-48fedd8be4ef/artifacts/3qpodzn8_logo%20rescue.jpg" 
                alt="The Safe House Logo" 
                className="w-14 h-14 object-contain rounded-lg"
              />
              <div className="-space-y-1">
                <div className="text-lg font-bold">The Safe House</div>
                <div className="text-xs text-gray-400">Children's Rescue Center</div>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t('footer.tagline', language)}
            </p>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} The Safe House Children's Rescue Center. {t('footer.allRightsReserved', language)}
            </p>
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <span>{language === 'en' ? 'Made with' : 'Hecho con'}</span>
              <Heart size={16} className="text-red-500" fill="currentColor" />
              <span>{language === 'en' ? 'for the children of Colombia' : 'para los niños de Colombia'}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
